/* global wasm_bindgen */
import { extractMolEvent } from '@moleculeone/draw';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import isEqual from 'react-fast-compare';
import * as Rx from 'rxjs';
import { map, scan } from 'rxjs/operators';

import { css, styled } from '../../../stitches.config';

import { SynthesisTreeSVG } from './SynthesisTreeSVG';
import { ResizeSensor } from './components/resize-sensor/resizeSensor';
import { calculateSvgScale } from './utils/calculateSvgScale';
import { HIGHLIGHTING_MODES } from './utils/highlighting';
import { prepareTree } from './utils/prepareTree';

const TreeContainer = styled('div', {
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  margin: '2rem 0',
});

const svgSpace = css({
  flexGrow: 1,
  maxWidth: '100%',
  maxHeight: 'calc(100vh - 22rem)',
});
const smallSvgSpace = css({
  maxHeight: '32rem',
});
const tinySvgSpace = css({
  maxHeight: '20rem',
});

export class SynthesisTree extends PureComponent {
  static propTypes = {
    tree: PropTypes.object,
    renderPointContext: PropTypes.shape({
      start: PropTypes.func,
      common: PropTypes.func,
      break: PropTypes.func,
      end: PropTypes.func,
      debug: PropTypes.func,
    }),
    renderMoleculeContext: PropTypes.func,
    bottomContext: PropTypes.node,
    synthesis: PropTypes.object,
    skipCache: PropTypes.bool,
    smilesToMol: PropTypes.func,
    id: PropTypes.string,
  };

  static defaultProps = {
    renderPointContext: {},
  };

  state = {
    nodes: {},
    edges: [],
    mapping: {},
    molCache: {},
    scale: 1,
  };

  async componentDidMount() {
    const inputStateAccumulatorSeed = {
      highlightedAtomUuid: null,
      highlightedMoleculeId: null,
    };

    this.inputStream = new Rx.Subject();
    this.inputSubscription = this.inputStream
      .pipe(
        map(e => {
          return this.hoverEventData(e);
        }),
        scan((acc, nxt) => ({ ...acc, ...nxt }), inputStateAccumulatorSeed),
        map(({ highlightedAtomUuid, highlightedMoleculeId }) => {
          return {
            highlightedAtomUuid,
            highlightedMoleculeId,
          };
        })
      )
      .subscribe(a => {
        this.setState(a);
      });

    this.listener = e => this.inputStream.next(e);
    document.addEventListener('mousemove', this.listener);
    document.addEventListener('keydown', this.listener);
    document.addEventListener('keyup', this.listener);

    this.onTreeUpdate();
    this.updateScale();
  }

  componentDidUpdate(prevProps) {
    const { renderPointContext, tree } = this.props;
    const { renderPointContext: prevRenderPointContext, tree: prevTree } = prevProps;

    const contextUpdated = !isEqual(renderPointContext, prevRenderPointContext);
    const treeUpdated = !isEqual(tree, prevTree);

    if (contextUpdated || treeUpdated) {
      this.onTreeUpdate();
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousemove', this.listener);
    document.removeEventListener('keydown', this.listener);
    document.removeEventListener('keyup', this.listener);
  }

  hoverEventData(e) {
    if (!e || !e.target) {
      return;
    }

    const closest = e.target.closest('.molecule-container');
    let targetMolecule;
    let moleculeId;
    let targetAtomUuid;

    if (closest) {
      const { mapping } = this.state;
      moleculeId = closest.getAttribute('data-molecule-id');
      const molEvent = extractMolEvent(e);

      const { atom, idx: atomIndex } = molEvent;
      if (atom) {
        targetMolecule = mapping[moleculeId] || [];
        targetAtomUuid = targetMolecule[atomIndex];
      }
    }
    return {
      highlightedMoleculeId: moleculeId,
      highlightedAtomUuid: targetAtomUuid,
    };
  }

  getSmilesById = id => {
    const { tree } = this.props;
    const node = tree[id];
    return node?.smiles ?? '';
  };

  onCopySmiles = async id => {
    const { tree } = this.props;
    const node = tree[id];
    const { smiles } = node;

    if (navigator.clipboard) {
      await navigator.clipboard.writeText(smiles);
    } else {
      const textarea = document.createElement('textarea');
      textarea.innerText = smiles;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      textarea.remove();
    }
  };

  async onTreeUpdate() {
    const { tree, renderPointContext, synthesis, skipCache, smilesToMol } = this.props;
    const { molCache } = this.state;

    if (Object.keys(tree).length === 0) {
      return;
    }

    const calculatedTree = await prepareTree({
      tree,
      renderPointContext,
      molCache: skipCache ? {} : molCache,
      smilesToMol: smilesToMol ?? wasm_bindgen?.smilesToMol,
      synthesis,
    });

    this.setState(calculatedTree);
  }

  updateScale(afterUpdateCallback = () => {}) {
    let scale = this.state.scale;
    const el = document.querySelector('#svg_container');
    if (el) {
      scale = calculateSvgScale(el);
    }

    this.setState(
      {
        scale,
      },
      () => {
        if (afterUpdateCallback) {
          afterUpdateCallback();
        }
      }
    );
  }

  render() {
    const { renderMoleculeContext, bottomContext, debugEnabled, id } = this.props;

    const { nodes, edges, mapping, highlightedMoleculeId, scale } = this.state;

    const isTreeRenderable = nodes && Object.keys(nodes).length > 0;

    return (
      <>
        <TreeContainer>
          {isTreeRenderable && (
            <ResizeSensor observeParents onResize={() => this.updateScale()}>
              <SynthesisTreeSVG
                mapping={mapping}
                nodes={nodes}
                edges={edges}
                highlightingMode={HIGHLIGHTING_MODES.SURVIVING_SUBSTRATE_ATOMS}
                highlightedMoleculeId={Number(highlightedMoleculeId)}
                onCopySmiles={this.onCopySmiles}
                onlyCopySmilesOptionVisible
                getSmilesById={this.getSmilesById}
                renderMoleculeContext={renderMoleculeContext}
                scale={scale}
                className={cx(svgSpace(), Object.keys(nodes).length === 1 ? tinySvgSpace() : smallSvgSpace())}
                debugEnabled={debugEnabled}
                id={id}
              />
            </ResizeSensor>
          )}
        </TreeContainer>
        {bottomContext}
      </>
    );
  }
}
